import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/skylight-replacement-phoenix";
const NAME = "Skylight Replacement";
const TITLE = "Skylight Replacement in Phoenix, AZ | Durafoam Roofing";
const DESCRIPTION =
  "Skylight replacement in Phoenix — fogged glass, yellowed domes, rotted curbs, repeat leaks. Velux solar-powered units, solar tubes, dome-to-glass swaps. Free inspection.";

const A = "font-semibold text-primary underline underline-offset-2 hover:text-accent";

const faqs = [
  {
    q: "Should I repair or replace my skylight?",
    a: "Repair when the glazing is sound and the problem is the flashing, gasket, or sealant — that's most leaks. Replace when the glass is fogged or cracked, the acrylic dome is yellowed or brittle, the curb is rotted, the unit is past 20 years, or it has leaked again after two repairs. At that point another repair is money spent on a unit that's going to be replaced anyway.",
    aNode: (
      <>
        Repair when the glazing is sound and the problem is the flashing, gasket, or sealant — that&apos;s
        most leaks, and it&apos;s covered on our{" "}
        <Link to="/skylight-repair" className={A}>
          skylight repair page
        </Link>
        . Replace when the glass is fogged or cracked, the acrylic dome is yellowed or brittle, the
        curb is rotted, the unit is past 20 years, or it has leaked again after two repairs. At that
        point another repair is money spent on a unit that&apos;s going to be replaced anyway.
      </>
    ),
  },
  {
    q: "How long do skylights last in Phoenix?",
    a: "Quality units run 20–25 years here. Acrylic domes yellow and craze under our UV faster than in milder climates, and the rubber gaskets and sealants dry out on roughly a 7–10 year cycle. A 25-year-old dome that has never been touched is living on borrowed time even if it isn't leaking yet.",
  },
  {
    q: "Can you replace an old plastic dome with glass?",
    a: "Usually yes. Most dome skylights sit on a curb, and modern curb-mount glass units — including Velux — are made to drop onto that same curb. We verify the curb size and condition first; if the curb is rotted or undersized, it gets rebuilt as part of the job. The upgrade is dramatic: clearer light, no yellowing, and far better heat performance with low-E glass.",
  },
  {
    q: "Will a new skylight leak?",
    a: "Not if the roof around it is done right. The skylight isn't what keeps water out — the flashing and the roof surface tying into the curb are. That's why we replace skylights as a roofing job, not a glass job: new curb flashing, correct laps and turn-ups, and on a foam roof, fresh foam and coating around the entire curb. Every replacement carries a written workmanship warranty on top of the manufacturer's warranty.",
  },
  {
    q: "Do solar-powered skylights qualify for a tax credit?",
    a: "Velux solar-powered skylights and solar blinds may qualify for the federal residential clean energy credit because they're powered by an onboard solar panel rather than household electricity. Rules and amounts change, so we confirm current eligibility at your inspection and hand over the manufacturer documentation you'd need to claim it. We're roofers, not tax advisors — check with your preparer.",
  },
  {
    q: "How long does a skylight replacement take?",
    a: "A straight dome-to-unit swap on an accessible roof is usually a few hours, and most homes with one or two skylights are finished the same day. Rebuilding a rotted curb, resizing an opening, or replacing several units takes one to two days. We don't leave an opening in your roof overnight.",
  },
  {
    q: "How much does skylight replacement cost in Phoenix?",
    a: "It depends almost entirely on whether the curb is reusable. A simple swap onto a sound curb is a fraction of a job that requires rebuilding the curb, reframing the opening, or repairing the roof deck around it — and venting or solar-powered units cost more than fixed ones. We look at it, tell you which case you're in, and put an exact number in writing after a free inspection.",
    aNode: (
      <>
        It depends almost entirely on whether the curb is reusable. A simple swap onto a sound curb is
        a fraction of a job that requires rebuilding the curb, reframing the opening, or repairing the
        roof deck around it — and venting or solar-powered units cost more than fixed ones. We look at
        it, tell you which case you&apos;re in, and put an exact number in writing after a free
        inspection. See{" "}
        <Link to="/skylight-repair-cost-phoenix" className={A}>
          skylight repair &amp; replacement costs in Phoenix
        </Link>
        .
      </>
    ),
  },
  {
    q: "Can you replace a skylight on a foam or tile roof?",
    a: "Yes, and the work is different on each. On foam, the curb is re-flashed and then re-foamed and recoated so the roof is seamless again. On tile, surrounding tile is lifted and reset with new underlayment and headwall flashing at the curb. On flat and modified-bitumen roofs, new base and counter-flashing is heat-welded or torched in. We do all three routinely.",
  },
  {
    q: "Can you just close up a skylight instead?",
    a: "Yes. Some homeowners are done with a chronically leaking skylight over a room that's already bright enough. We remove the unit, frame and deck the opening, and continue the roof system across it so there's no trace of it outside. If you still want daylight but not the skylight, a solar tube is often the answer.",
  },
];

export const Route = createFileRoute("/skylight-replacement-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({
      override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="skylight-replacement-phoenix"
      extraSections={SEARCH_TERM_SECTIONS["skylight-replacement-phoenix"]}
      eyebrow="Skylights"
      h1="Skylight Replacement in Phoenix"
      subhead="Fogged glass, yellowed domes, rotted curbs, and skylights that leak no matter how many times they've been sealed — replaced as a roofing job, by roofers, with the flashing done right."
      heroBullets={[
        "Dome-to-glass and Velux upgrades",
        "Curb rebuild & flashing included",
        "Solar-powered units and solar tubes",
        "Free inspection with photos",
      ]}
      whatIs={{
        heading: "When replacement beats another repair",
        body: (
          <>
            <p>
              We repair far more skylights than we replace, because most skylight leaks are flashing
              and gasket failures on a unit that&apos;s otherwise fine. But there&apos;s a point where
              resealing is throwing money at a part that has aged out. These are the signs:
            </p>
            <ul>
              <li>
                <strong>Fogged or hazy glass.</strong> The seal between the panes has failed and
                moisture is inside the unit. There is no repair for this — the glazing is spent.
              </li>
              <li>
                <strong>Yellowed, chalky, or crazed acrylic domes.</strong> Phoenix UV does this to
                every plastic dome eventually. Once it&apos;s brittle it cracks the moment anything
                lands on it, and it dims the room it was installed to brighten.
              </li>
              <li>
                <strong>Cracked glazing or a hail strike.</strong> Replace it — and document it, since
                it may be a{" "}
                <Link to="/storm-damage-repair" className={A}>
                  storm damage claim
                </Link>
                .
              </li>
              <li>
                <strong>A rotted or crumbling curb.</strong> The wooden box the skylight sits on is
                the waterproofing detail. If it&apos;s soft, nothing bolted to it will stay dry.
              </li>
              <li>
                <strong>Repeat leaks after two or more repairs.</strong> At that point the unit,
                curb, or both are the problem, not the sealant.
              </li>
              <li>
                <strong>Age past 20–25 years.</strong> Especially if you&apos;re already having roof
                work done — replacing a skylight while the crew is up there costs far less than
                doing it as its own trip later.
              </li>
              <li>
                <strong>You want features the old unit doesn&apos;t have.</strong> Venting, remote
                operation, a built-in shade, low-E glass that keeps the room from cooking in July.
              </li>
            </ul>
            <p>
              Not sure which side of that line you&apos;re on? That&apos;s exactly what a{" "}
              <Link to="/free-roof-inspection-phoenix" className={A}>
                free roof inspection
              </Link>{" "}
              settles — with photos, so you can see what we saw.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "What actually gets replaced (and why the roof matters more than the skylight)",
        body: (
          <>
            <p>
              A skylight replacement is a roofing job with a glass component, not a window swap.
              Nearly every skylight leak we&apos;re called out to fix was created by someone treating
              it as the latter. Here&apos;s the full scope of the work:
            </p>
            <ul>
              <li>
                <strong>The unit.</strong> Removed and replaced with a matched or upgraded skylight —
                fixed or venting, glass or dome, sized to the existing opening where possible.
              </li>
              <li>
                <strong>The curb.</strong> Inspected, repaired, rebuilt, or raised as needed. A curb
                that&apos;s too low for the roof system will always be a leak waiting to happen.
              </li>
              <li>
                <strong>The deck around it.</strong> Any soft or water-damaged sheathing beside the
                curb gets cut out and replaced. This is where old leaks hide.
              </li>
              <li>
                <strong>The flashing.</strong> New head, side, and sill flashing, properly lapped and
                turned up the curb — the single detail that decides whether the new skylight leaks.
              </li>
              <li>
                <strong>The surrounding roof surface.</strong> Foam and coating re-sprayed on a foam
                roof; tile lifted and reset with fresh underlayment on tile; base and counter-flashing
                welded in on modified bitumen or TPO.
              </li>
              <li>
                <strong>The interior shaft, when needed.</strong> Drywall and trim at the light well
                repaired where water has already done damage.
              </li>
            </ul>
            <p>
              If your roof is also nearing the end of its life, replacing skylights during the{" "}
              <Link to="/roof-replacement-phoenix" className={A}>
                roof replacement
              </Link>{" "}
              or{" "}
              <Link to="/foam-roof-coating-recoating" className={A}>
                recoat
              </Link>{" "}
              is the cheapest time to do it. There&apos;s no second mobilization and no patching a
              brand-new roof.
            </p>
          </>
        ),
      }}
      process={{
        heading: "How a skylight replacement goes",
        intro:
          "Same-day on most homes. We don't open a roof we can't close before we leave.",
        steps: [
          {
            title: "Free inspection and measurement",
            body: "We check the glazing, curb, flashing, deck, and the roof system around it, photograph everything, and measure the opening so the new unit is specified correctly the first time.",
          },
          {
            title: "Written options, not one price",
            body: "You get pricing for the sensible choices — like-for-like swap, glass upgrade, venting or solar-powered unit, or closing the opening entirely — so you can decide on value rather than guessing.",
          },
          {
            title: "Removal and curb work",
            body: "Old unit out, curb and deck inspected in the open and repaired or rebuilt on the spot. Interior protected below.",
          },
          {
            title: "Set the unit and flash it in",
            body: "New skylight set, fastened, and flashed to manufacturer detail, then the surrounding roof system restored — foam and coating, reset tile, or welded membrane.",
          },
          {
            title: "Water test, cleanup, warranty",
            body: "We test it with water before we call it done, clean the site, and register the manufacturer warranty alongside our written workmanship warranty.",
          },
        ],
      }}
      extraSection={{
        heading: "Your options: glass, domes, venting, solar-powered, and solar tubes",
        body: (
          <>
            <p>
              Replacement is the one chance you get to fix what you never liked about the old
              skylight. The choices that matter in Phoenix:
            </p>
            <ul>
              <li>
                <strong>Fixed vs. venting.</strong> Fixed units are light only and the simplest to
                keep dry. Venting units add real airflow — worth it over kitchens, baths, and stairwells
                where heat and humidity collect.
              </li>
              <li>
                <strong>Glass vs. acrylic dome.</strong> Modern low-E laminated glass stays clear for
                decades, cuts heat gain, and doesn&apos;t yellow. Domes are cheaper up front and still
                the practical choice on some low-slope curbs, but they&apos;re a consumable in this
                climate.
              </li>
              <li>
                <strong>Curb-mount vs. deck-mount.</strong> Most Phoenix homes have curb-mount
                openings, which makes an upgrade straightforward. Deck-mount units sit lower and look
                sleeker on steeper roofs.
              </li>
              <li>
                <strong>Velux solar-powered skylights with built-in sun shade.</strong> Our most
                popular upgrade. An onboard solar panel runs the unit, so there&apos;s no wiring to
                run: it opens and closes by remote, the built-in shade is remote-controlled too, and a
                rain sensor closes it automatically when a monsoon rolls in. They&apos;re designed as a
                direct swap for existing dome skylights, so most installs need no framing changes.
              </li>
              <li>
                <strong>Solar tubes and sun tunnels.</strong> The low-cost daylight option — a
                reflective tube that pipes sunlight into hallways, closets, laundry rooms, and baths
                where a full skylight won&apos;t fit or isn&apos;t worth the cost. No open view of the
                sky, far less heat gain, and much simpler to keep watertight.
              </li>
              <li>
                <strong>Closing the opening.</strong> A perfectly legitimate choice for a chronic
                leaker over a room that already has plenty of light.
              </li>
            </ul>
            <p>
              On cost: the range is wide because the work is. A dome swap onto a sound curb is a
              modest job; a solar-powered glass unit with a rebuilt curb is a real project. Rather than
              quote a number that won&apos;t match your roof, we&apos;ll look at it — the inspection is
              free and ends with a written price. See{" "}
              <Link to="/skylight-repair-cost-phoenix" className={A}>
                skylight costs in Phoenix
              </Link>{" "}
              for ranges, and{" "}
              <Link to="/financing" className={A}>
                cost solutions
              </Link>{" "}
              if you&apos;d rather spread it out.
            </p>
          </>
        ),
      }}
      whyChoose={{
        heading: "Why have roofers replace your skylight",
        body: (
          <>
            <ul>
              <li>
                <strong>The leak risk lives in the flashing.</strong> We&apos;re a roofing company
                first — the skylight is the easy part of the job.
              </li>
              <li>
                <strong>Family-owned since 1989, licensed ROC223900.</strong> Thousands of Valley
                roofs, and skylights on most of them.
              </li>
              <li>
                <strong>Foam, tile, shingle, and flat all in-house.</strong> Whatever your roof is,
                the surface goes back correctly around the new curb.
              </li>
              <li>
                <strong>We&apos;ll tell you to repair it</strong> when repair is the right answer. Most
                skylight calls end that way — see{" "}
                <Link to="/skylight-repair" className={A}>
                  skylight repair
                </Link>
                .
              </li>
              <li>
                <strong>Written workmanship warranty</strong> plus manufacturer coverage, and photos of
                the flashing before it&apos;s covered up.
              </li>
            </ul>
          </>
        ),
      }}
      faqs={faqs}
      ctaLabel="Book a Free Skylight Inspection"
      ctaTitle="Fogged, cracked, or leaking again?"
      ctaBody="We'll look at the glazing, the curb, and the roof around it, then give you written options — repair or replace, no pressure either way."
    />
  );
}
